import styles from './BookedState.module.scss';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Tag,
  TagColor,
  TagSize,
} from '@spotted-zebra-uk/ui-components';
import { BookingDetails } from '../../api/bookingApi.types';
import { formatDayFull, formatSlotRange } from '../../timezone';
import { useFocusOnMount } from '../../useFocusOnMount';
import { blockedReasonKey } from '../BookedFlow/candidateActionCopy';
import BookedMaterials from './BookedMaterials';
import CalendarDownload from './CalendarDownload';
import InterviewLine from './InterviewLine';
import JoinSection from './JoinSection';

interface BookedStateProps {
  booking: BookingDetails;
  token: string;
  timeZone: string;
  /** Moves focus to the heading, for a booking made by pressing Confirm. */
  focusHeading?: boolean;
  /**
   * Opens the change-your-interview flow. Offered only while the booking
   * read says the candidate may still cancel or move the interview; when
   * it says neither, the reason is shown instead.
   */
  onManage?: () => void;
}

/**
 * The booked page (BL-1513, BL-1521, BL-1593): the interview's time, who it
 * is with and for how long, where to join, the preparation materials, the
 * calendar entry, the way to change it and the note-taker disclosure. It
 * shows only what the API returns for the candidate's own link, which
 * carries no name or address of anyone on the hiring team.
 */
const BookedState: FC<BookedStateProps> = ({
  booking,
  token,
  timeZone,
  focusHeading = false,
  onManage,
}) => {
  const { t } = useTranslation();
  const headingRef = useFocusOnMount<HTMLHeadingElement>(focusHeading);
  const canManage = booking.canCancel || booking.canReschedule;
  const blockedKey = canManage ? null : blockedReasonKey(booking.blockedReason);

  return (
    <div className={styles.wrapper}>
      <div className={styles.heading}>
        <Tag
          tagColor={TagColor.GREEN}
          size={TagSize.LARGE}
          isPresentational
          isDismissible={false}
        >
          {t('publicScheduling.bookedTag')}
        </Tag>
        <h1
          className={styles.title}
          ref={headingRef}
          tabIndex={focusHeading ? -1 : undefined}
        >
          {t('publicScheduling.bookedTitle', {
            day: formatDayFull(booking.startsAt, timeZone),
            range: formatSlotRange(booking, timeZone),
          })}
        </h1>
        <InterviewLine
          roleName={booking.roleName}
          companyName={booking.companyName}
        />
        <p className={styles.subline}>
          {t(
            booking.conferencing === 'teams'
              ? 'publicScheduling.bookedSublineTeams'
              : 'publicScheduling.bookedSubline',
            { minutes: booking.durationMinutes }
          )}
        </p>
      </div>
      <JoinSection joinState={booking.joinState} joinUrl={booking.joinUrl} />
      <BookedMaterials materials={booking.materials} />
      <CalendarDownload token={token} offered={booking.calendarFileOffered} />
      {canManage && onManage && (
        <Button variant="tertiary" size="medium" onClick={onManage}>
          {t('publicScheduling.rescheduleOrCancel')}
        </Button>
      )}
      {blockedKey && <p className={styles.note}>{t(blockedKey)}</p>}
      {booking.spotJoins && (
        <p className={styles.note}>
          {t('publicScheduling.noteTakerDisclosure')}
        </p>
      )}
    </div>
  );
};

export default BookedState;
